/**
 * @currencycore/shared
 *
 * Single source of truth for the CurrencyCore.dev API contract.
 * Imported by the Hono API (apps/api), the Astro site (apps/web),
 * and the dashboard SPA (apps/dashboard). Source-only — consumed via
 * the bundler/TS, no build step.
 */

// ---------------------------------------------------------------------------
// Primitive aliases
// ---------------------------------------------------------------------------

/** ISO 4217 currency code, e.g. "USD", "INR", "EUR". */
export type CurrencyCode = string;

/** The curated set of major currencies the marketing site (home page + /web/rates) exposes. */
export const WEB_RATES_CURRENCIES = [
  "USD", "EUR", "GBP", "INR", "JPY", "AUD", "CAD", "SGD", "AED", "BRL", "CNY", "MXN",
] as const;

/** ISO 3166-1 alpha-2 country code, e.g. "US", "IN", "DE". */
export type CountryCode = string;

/** YYYY-MM-DD. */
export type IsoDate = string;

/**
 * A flat map of currency -> rate, where the value is the number of units of
 * that currency per 1 USD. USD is the base, so `USD` is implicitly 1.
 * Example: { "INR": 83.45, "EUR": 0.92 }.
 *
 * NOTE: the D1 column is named `rate_to_usd` per the spec, but the stored
 * value is the USD-base multiplier (units-per-USD). The triangulation formula
 * below is consistent with that interpretation.
 */
export type RatesMap = Record<CurrencyCode, number>;

// ---------------------------------------------------------------------------
// /convert contract
// ---------------------------------------------------------------------------

export interface ConvertQuery {
  /**
   * Source currency. When `ppp=true`, pair it with its country as
   * `currency:country` (e.g. `USD:USA`) — see [/countries] for the codes.
   */
  from: string;
  /**
   * One or more target currencies, comma-separated. Each item is a bare currency
   * (`INR`) or — when `ppp=true` — a `currency:country` pair (`INR:IND`). The pair
   * form is how the SAME currency targets multiple countries, since PPP is
   * country-specific: `to=EUR:DEU,EUR:FRA` yields two distinct results.
   */
  to: string;
  amount: number;
  /** Defaults to the latest available rate date. */
  date?: IsoDate;
  /**
   * When true, EVERY currency (source + each target) must carry a country code,
   * passed as `currency:country`. No separate country params.
   */
  ppp?: boolean;
}

/**
 * PPP detail for a target when PPP COULD be applied: the source/target factors
 * plus the PPP-adjusted amount.
 */
export interface PppFactors {
  /** Source country's PPP conversion factor (local units per international $). */
  fromFactor: number;
  /** Target country's PPP conversion factor (local units per international $). */
  toFactor: number;
  /**
   * The PPP-adjusted amount: `amount * (toFactor / fromFactor)`. A pure
   * price-level conversion via the international dollar — it does NOT use the
   * nominal FX rate (PPP replaces the market rate, it doesn't compound it).
   */
  result: number;
  /** Year of the PPP conversion factors used. */
  year: number;
  /** Provider of the PPP conversion factors (IMF World Economic Outlook, PPPEX). */
  source: string;
}

/**
 * Per-target PPP error: PPP could not be applied for this target (unknown/
 * mismatched country, missing PPP data, etc.). The plain FX `result`/`rate`
 * on the target are still returned — only the PPP layer failed.
 */
export interface PppError {
  error: string;
}

/**
 * PPP detail for a target (present whenever PPP was requested): either the
 * factors + adjusted amount, or a per-target `error` explaining why PPP could
 * not be applied. Omitted entirely when PPP was not requested.
 */
export type PppDetail = PppFactors | PppError;

/** Provider of our PPP conversion factors (IMF WEO "Implied PPP conversion rate", PPPEX). */
export const PPP_SOURCE = "IMF World Economic Outlook";

/** One converted target — a currency, plus its PPP detail when PPP is requested. */
export interface ConvertTarget {
  to: CurrencyCode;
  /** PPP country for THIS target. Present only when PPP was requested. */
  toCountry?: CountryCode;
  /** Plain FX-converted amount (never PPP-adjusted — see `ppp.result` for that). */
  result: number;
  /** Effective FX rate applied (result / amount). */
  rate: number;
  /**
   * The PPP detail: factors + adjusted amount, or a per-target `error`. Omitted
   * entirely when `ppp` was not requested.
   */
  ppp?: PppDetail;
}

/**
 * Per-currency "as-of" map: a currency code → the earlier date its rate was
 * carried forward from, for currencies that had no quote on the served `date`
 * (e.g. a currency suspended/not-yet-listed). Present (non-empty) only when at
 * least one rate in the response is a carried-forward value.
 */
export type RatesAsOf = Record<CurrencyCode, IsoDate>;

export interface ConvertResponse {
  from: CurrencyCode;
  /** Source country. Present only when PPP was requested (shared by all results). */
  fromCountry?: CountryCode;
  amount: number;
  /** Rate date actually used (UTC) — see `requestedDate` when carried back. The PPP year is derived from this date's year. */
  date: IsoDate;
  /**
   * The date the caller asked for, present ONLY when it differs from `date` —
   * i.e. the requested date had no snapshot (weekend/holiday/gap) so the most
   * recent earlier snapshot was used instead.
   */
  requestedDate?: IsoDate;
  /** Carried-forward currencies among `from`/targets → their origin date. Omitted when none. */
  asOf?: RatesAsOf;
  /** One entry per requested target. Empty when no FX data exists for `date`. */
  results: ConvertTarget[];
  /** Set when no FX data exists for the requested date (`results` is empty). */
  message?: string;
}

// ---------------------------------------------------------------------------
// /rates and /ppp contracts
// ---------------------------------------------------------------------------

export interface RatesResponse {
  /** Rate date actually used (UTC) — see `requestedDate` when carried back. */
  date: IsoDate;
  base: "USD";
  rates: RatesMap;
  /** Present ONLY when it differs from `date`: the requested date had no snapshot, so an earlier one was used. */
  requestedDate?: IsoDate;
  /** Carried-forward currencies → their origin date. Omitted when none. */
  asOf?: RatesAsOf;
}

export interface PppResponse {
  year: number;
  countryCode: CountryCode;
  pppFactor: number;
  /** True if this is an IMF projection (forecast year) rather than actual. */
  projected: boolean;
}

/**
 * GET /rates/:base — every currency's rate expressed against a chosen base
 * currency (not a conversion of an amount, just the rebased snapshot). When
 * `ppp=true` and the base includes a country (`USD:USA`), `ppp` carries each
 * country's PPP price level relative to the base country (base = 1.0).
 */
export interface RatesByBaseResponse {
  /** Base currency every rate is expressed against (e.g. "USD", "EUR"). */
  base: CurrencyCode;
  /** Rate date actually used (UTC) — see `requestedDate` when carried back. */
  date: IsoDate;
  /** Present ONLY when it differs from `date`: the requested date had no snapshot, so an earlier one was used. */
  requestedDate?: IsoDate;
  /** Carried-forward currencies → their origin date. Omitted when none. */
  asOf?: RatesAsOf;
  /** Units of each currency per 1 unit of `base` (`base` itself = 1). */
  rates: RatesMap;
  ppp?: {
    /** Base country (alpha-3) the PPP values are relative to. */
    base: CountryCode;
    year: number;
    /** PPP price level of each country relative to the base country (base = 1). */
    values: Record<CountryCode, number>;
  };
}

/** Default look-back window (days) for /history when `from` is omitted. */
export const HISTORY_DEFAULT_RANGE_DAYS = 7;

/**
 * Aggregation interval for /history. `daily` (default) returns one entry per
 * calendar day; the others average the daily rates within each bucket. Bucket
 * keys: weekly = the week's Monday (YYYY-MM-DD), monthly = YYYY-MM, yearly = YYYY.
 */
export type HistoryInterval = "daily" | "weekly" | "monthly" | "yearly";

/** Allowed /history `interval` values, in order. */
export const HISTORY_INTERVALS: readonly HistoryInterval[] = ["daily", "weekly", "monthly", "yearly"];

/**
 * GET /history — a single currency's exchange-rate time series, rebased to `base`.
 * For `interval=daily` the map is dense (one entry per calendar day in [from, to],
 * gaps carried forward) and includes today even before the end-of-day bake runs.
 * Coarser intervals average the daily rates within each bucket. Growth+ only.
 */
export interface HistoryResponse {
  /** The currency whose series this is (3-letter ISO 4217). */
  currency: CurrencyCode;
  /** Base the rates are expressed against (units of `currency` per 1 `base`). USD by default. */
  base: CurrencyCode;
  /** Aggregation interval applied to the series. */
  interval: HistoryInterval;
  /** Inclusive start of the returned window (YYYY-MM-DD, UTC). */
  from: IsoDate;
  /** Inclusive end of the returned window (YYYY-MM-DD, UTC). */
  to: IsoDate;
  /**
   * bucket → rate, ascending. For `daily`, keys are dates (a day before the
   * currency's first quote is absent); for coarser intervals, keys are the period
   * label (week's Monday / YYYY-MM / YYYY) and the value is the average of that
   * period's daily rates within the window.
   */
  rates: Record<string, number>;
}

/** Ranking direction for GET /history/analysis "movers". */
export type AnalysisSort = "strengthened" | "weakened";

/**
 * Asset class filter for "movers" rankings. `fiat` (default) ranks real
 * national currencies only; `metals` ranks precious-metal codes; `all` includes
 * everything (metals + SDR); `crypto` has no data in our FX dataset (empty).
 */
export type AssetClass = "fiat" | "metals" | "all" | "crypto";
export const ASSET_CLASSES: readonly AssetClass[] = ["fiat", "metals", "all", "crypto"];

/** Precious-metal "currency" codes (priced as troy-ounce-per-USD), not fiat. */
export const METAL_ASSETS: readonly string[] = ["XAU", "XAG", "XPT", "XPD"];

/**
 * Whether a code is a precious-metal asset (XAU/XAG/XPT/XPD). CurrencyCore's rate
 * source (ECB / Frankfurter) carries no metal prices, so metals have no rate or
 * history data and are treated as unsupported rather than returning empty data.
 */
export function isPreciousMetal(code: string): boolean {
  return METAL_ASSETS.includes(code.toUpperCase());
}

/** User-facing message when a precious metal is requested (no data source for it). */
export const PRECIOUS_METALS_UNSUPPORTED =
  "CurrencyCore doesn't have precious-metal prices (gold/XAU, silver/XAG, platinum/XPT, palladium/XPD); it covers currencies, FX rates, and PPP.";

/** Synthetic / non-national units (IMF SDR and bond-market baskets) — not fiat. */
export const SYNTHETIC_ASSETS: readonly string[] = ["XDR", "XSU", "XUA", "XBA", "XBB", "XBC", "XBD", "XTS", "XXX"];

/** Everything excluded from a `fiat` movers ranking (metals + synthetic units). */
export const NON_FIAT_ASSETS: ReadonlySet<string> = new Set([...METAL_ASSETS, ...SYNTHETIC_ASSETS]);

/**
 * Legacy ISO codes for currencies that have been REPLACED by a redenominated
 * successor (e.g. BYR→BYN ÷10,000, TRL→TRY ÷1,000,000). They only exist in the
 * pre-cut data, so excluding them from a ranking is always safe and never drops
 * a live currency. The successor codes are handled window-aware by the absurd-
 * change guard below (a redenomination span shows an impossible % move).
 */
/**
 * A currency REDENOMINATION (a unit change like BYR→BYN ÷10,000). When a "movers"
 * ranking window SPANS the redenomination date, the rate series is stitched
 * across the unit change, so any % move over that window is a meaningless
 * artifact — e.g. BYN appears to "strengthen 217,800%". Both the legacy and the
 * successor ISO codes are keyed so a ranking in EITHER era is caught. This is
 * window-aware (see {@link crossesRedenomination}): a currency whose window is
 * entirely AFTER its redenomination (e.g. TRY since 2015) is NOT excluded.
 */
export interface Redenomination {
  /** Effective date of the unit change (YYYY-MM-DD, UTC). */
  date: IsoDate;
  /** The ISO code it replaced, when this entry is the successor. */
  previous?: string;
  /** Old units per 1 new unit. */
  factor?: number;
}

export const REDENOMINATIONS: Record<string, Redenomination> = {
  BYN: { date: "2016-07-01", previous: "BYR", factor: 10_000 },
  BYR: { date: "2016-07-01", factor: 10_000 },
  MRU: { date: "2018-01-01", previous: "MRO", factor: 10 },
  MRO: { date: "2018-01-01", factor: 10 },
  VES: { date: "2018-08-20", previous: "VEF", factor: 100_000 },
  VEF: { date: "2018-08-20", factor: 100_000 },
  VED: { date: "2021-10-01", previous: "VES", factor: 1_000_000 },
  ZMW: { date: "2013-01-01", previous: "ZMK", factor: 1_000 },
  ZMK: { date: "2013-01-01", factor: 1_000 },
  TRY: { date: "2005-01-01", previous: "TRL", factor: 1_000_000 },
  TRL: { date: "2005-01-01", factor: 1_000_000 },
  RON: { date: "2005-07-01", previous: "ROL", factor: 10_000 },
  ROL: { date: "2005-07-01", factor: 10_000 },
  GHS: { date: "2007-07-01", previous: "GHC", factor: 10_000 },
  GHC: { date: "2007-07-01", factor: 10_000 },
  AZN: { date: "2006-01-01", previous: "AZM", factor: 5_000 },
  AZM: { date: "2006-01-01", factor: 5_000 },
  MZN: { date: "2006-07-01", previous: "MZM", factor: 1_000 },
  MZM: { date: "2006-07-01", factor: 1_000 },
  SDG: { date: "2007-01-10", previous: "SDD", factor: 100 },
  SDD: { date: "2007-01-10", factor: 100 },
  TMT: { date: "2009-01-01", previous: "TMM", factor: 5_000 },
  TMM: { date: "2009-01-01", factor: 5_000 },
  ZWL: { date: "2009-02-02", previous: "ZWR", factor: 1_000_000_000_000 },
  ZWR: { date: "2008-08-01", factor: 10_000_000_000 },
  ZWD: { date: "2006-08-01", factor: 1_000 },
};

/**
 * True when a redenomination for `code` falls inside the ranking window (from, to]
 * — i.e. the rate series is stitched across a unit change, so any % move over the
 * window is an artifact. A window entirely before OR entirely after the event is
 * clean (returns false).
 */
export function crossesRedenomination(code: string, from: IsoDate, to: IsoDate): boolean {
  const r = REDENOMINATIONS[code.toUpperCase()];
  return !!r && r.date > from && r.date <= to;
}

/**
 * Backstop for any redenomination NOT in the table above: a "movers" move larger
 * than this (in EITHER direction — % change OR strengthening) can't be a genuine
 * fiat move and is dropped. A ÷1,000 redenomination is ~100,000%; this catches
 * the extreme ones the metadata misses. (Small ÷10 redenominations look like real
 * moves and rely on the metadata, not this threshold.)
 */
export const MOVERS_MAX_ABS_CHANGE_PCT = 100_000;

/**
 * The "major" currencies (and, vs USD, the major pairs) — for queries like "most
 * volatile MAJOR pair". A narrow, conventional set distinct from the broader
 * liquid universe (which includes RUB, TRY, etc.).
 */
export const MAJOR_CURRENCIES: readonly CurrencyCode[] = ["USD", "EUR", "GBP", "JPY", "CHF", "CAD", "AUD", "NZD"];

/**
 * Currencies on a hard PEG or tight managed float, with what they track. A move
 * vs some third currency is then mostly the OTHER currency moving, so the AI
 * should note the peg rather than imply the pegged currency itself appreciated.
 */
export const PEGGED_CURRENCIES: Record<string, { peggedTo: string }> = {
  AED: { peggedTo: "USD" }, // 3.6725
  SAR: { peggedTo: "USD" },
  QAR: { peggedTo: "USD" },
  OMR: { peggedTo: "USD" },
  BHD: { peggedTo: "USD" },
  HKD: { peggedTo: "USD" },
  DKK: { peggedTo: "EUR" },
  BGN: { peggedTo: "EUR" },
  XOF: { peggedTo: "EUR" },
  XAF: { peggedTo: "EUR" },
};

/**
 * Currencies conventionally treated as SAFE HAVENS — they tend to hold or gain
 * value when markets are stressed. The AI notes this when one tops a stability
 * or low-drawdown ranking (e.g. "CHF, a classic safe haven").
 */
export const SAFE_HAVEN_CURRENCIES: readonly CurrencyCode[] = ["USD", "CHF", "JPY", "SGD"];

/**
 * Per-currency reference metadata the analytics layer attaches automatically so
 * an answer can EXPLAIN a result, not just state it: a USD-pegged currency's
 * "move" vs a third currency is the other side moving (AED/SAR/HKD → USD), a
 * safe haven's low drawdown is expected (CHF), and a redenominated currency's
 * long-run figures are unit-change artifacts (ROL→RON).
 */
export interface CurrencyMeta {
  currency: CurrencyCode;
  /** One of the conventional "major" currencies. */
  major: boolean;
  /** fiat | metals (XAU/XAG/…) | synthetic (SDR/baskets). */
  assetClass: "fiat" | "metals" | "synthetic";
  /** Hard peg or tight managed float. */
  pegged: boolean;
  /** What it's pegged to, when pegged (USD/EUR), else null. */
  pegTarget: string | null;
  /** A conventional safe-haven currency. */
  safeHaven: boolean;
  /** Underwent a unit redenomination at some point (long-run series stitched). */
  redenominated: boolean;
}

/** Assemble {@link CurrencyMeta} for a code from the reference registries. */
export function currencyMeta(code: string): CurrencyMeta {
  const c = code.toUpperCase();
  const assetClass: CurrencyMeta["assetClass"] = (METAL_ASSETS as readonly string[]).includes(c)
    ? "metals"
    : (SYNTHETIC_ASSETS as readonly string[]).includes(c)
      ? "synthetic"
      : "fiat";
  const peg = PEGGED_CURRENCIES[c];
  return {
    currency: c,
    major: (MAJOR_CURRENCIES as readonly string[]).includes(c),
    assetClass,
    pegged: !!peg,
    pegTarget: peg?.peggedTo ?? null,
    safeHaven: (SAFE_HAVEN_CURRENCIES as readonly string[]).includes(c),
    redenominated: !!REDENOMINATIONS[c],
  };
}

/** min/max/avg of a currency's rate (units per 1 base) over the window. */
export interface HistoryAnalysisStats {
  min: number;
  max: number;
  /** Mean over the window's trading days. */
  avg: number;
}

/** One currency's analysis over the window (rates are units of currency per 1 base). */
export interface HistoryAnalysisResult {
  currency: CurrencyCode;
  /** Snapshot date used for the start of the window (carry-back resolved). */
  startDate: IsoDate;
  /** Snapshot date used for the end of the window. */
  endDate: IsoDate;
  /** Rate at the start; null when the currency had no data then. */
  startRate: number | null;
  /** Rate at the end; null when no data. */
  endRate: number | null;
  /** % change in the quoted (units-per-base) rate. null when start/end missing. */
  changePct: number | null;
  /** % the currency appreciated vs base (positive = strengthened). null when missing. */
  strengthenedPct: number | null;
  /** min/max/avg over the window — present only when `stats` was requested. */
  stats?: HistoryAnalysisStats;
  /** Aggregated trend series (period → avg rate) — present only for named currencies with an `interval`. */
  series?: Record<string, number>;
  /** Annualized volatility (%) over the window — present in compare mode (for reports). */
  volatility?: number | null;
  /** Why a value is missing (e.g. the currency had no data at the window start). */
  note?: string;
}

/**
 * GET /history/analysis — trend / comparison / "movers" over the historical rates.
 * Provide `currencies` to compare a set; omit it to rank ALL currencies vs `base`.
 * Growth+ only.
 */
export interface HistoryAnalysisResponse {
  base: CurrencyCode;
  /** Inclusive window start (YYYY-MM-DD, UTC). */
  from: IsoDate;
  /** Inclusive window end (YYYY-MM-DD, UTC). */
  to: IsoDate;
  /** `compare` when `currencies` were named; `movers` when ranking all. */
  mode: "compare" | "movers";
  /** Ranking direction (movers mode only). */
  sort?: AnalysisSort;
  /** Asset class the movers ranking was filtered to (movers mode only). */
  assetClass?: AssetClass;
  /** Trend-series interval, when requested. */
  interval?: HistoryInterval;
  /** Whether min/max/avg stats were computed. */
  stats: boolean;
  /** Per-currency analysis (ranked + limited in movers mode). */
  results: HistoryAnalysisResult[];
}

/** Default look-back window (years) for /ppp/analysis when neither from nor period is given. */
export const PPP_ANALYSIS_DEFAULT_YEARS = 10;

/** Ranking direction for GET /ppp/analysis "movers" — by signed PPP-factor change. */
export type PppAnalysisSort = "increased" | "decreased";

/** min/max/avg of a country's PPP factor over the window's years. */
export interface PppAnalysisStats {
  min: number;
  max: number;
  /** Mean over the window's available years. */
  avg: number;
}

/**
 * One country's PPP analysis over [fromYear, toYear]. The PPP factor is local
 * currency units per international dollar (same convention as /ppp and
 * /convert?ppp=true) — a RISING factor means the country's price level rose
 * relative to the international dollar over the window.
 */
export interface PppAnalysisResult {
  /** ISO 3166-1 alpha-3 country code. */
  country: CountryCode;
  /** Year actually used for the window start (earliest available year ≥ from). */
  startYear: number;
  /** Year actually used for the window end (latest available year ≤ to). */
  endYear: number;
  /** PPP factor at startYear; null when the country has no data in the window. */
  startFactor: number | null;
  /** PPP factor at endYear; null when no data. */
  endFactor: number | null;
  /** % change in the PPP factor over the window. null when start/end missing. */
  changePct: number | null;
  /** True when the endYear value is an IMF projection (forecast), not actual. */
  projected: boolean;
  /** min/max/avg over the window — present only when `stats` was requested. */
  stats?: PppAnalysisStats;
  /** Year → factor trend — present only for named countries (compare mode). */
  series?: Record<string, number>;
  /** Why a value is missing (e.g. the country had no PPP data in the window). */
  note?: string;
}

/**
 * GET /ppp/analysis — PPP trend / comparison / "movers" over the yearly IMF PPP
 * conversion factors. Provide `countries` to compare a set; omit to rank ALL
 * countries by PPP-factor change. Growth+ only.
 */
export interface PppAnalysisResponse {
  /** Inclusive window start year. */
  from: number;
  /** Inclusive window end year. */
  to: number;
  /** `compare` when `countries` were named; `movers` when ranking all. */
  mode: "compare" | "movers";
  /** Ranking direction (movers mode only). */
  sort?: PppAnalysisSort;
  /** Whether min/max/avg stats were computed. */
  stats: boolean;
  /** Per-country PPP analysis (ranked + limited in movers mode). */
  results: PppAnalysisResult[];
}

// --- Analytics endpoints (volatility / correlation / drawdown) --------------
// All measured over the daily rate series rebased to `base`. Growth+ only.

/** Default look-back window (days) for the analytics endpoints when `from` is omitted. */
export const ANALYTICS_DEFAULT_RANGE_DAYS = 365;

/**
 * A curated set of liquid currencies used as the universe for an analytics
 * "rank all" request (e.g. "most stable currencies"). Ranking literally every
 * code would mean loading every currency's full history; this bounds it to the
 * majors + actively-traded EM currencies people actually compare.
 */
export const LIQUID_FIAT_CURRENCIES: readonly CurrencyCode[] = [
  "USD", "EUR", "JPY", "GBP", "AUD", "CAD", "CHF", "CNY", "HKD", "SGD",
  "SEK", "NOK", "NZD", "MXN", "INR", "ZAR", "BRL", "RUB", "KRW", "TRY",
  "PLN", "THB", "IDR", "HUF", "CZK", "ILS", "CLP", "PHP", "AED", "SAR",
  "MYR", "RON", "DKK", "COP", "TWD",
];

/** Volatility ranking direction: `stable` (lowest first, default) or `volatile`. */
export type VolatilitySort = "stable" | "volatile";

/** Universe for a volatility RANKING: the broad liquid set (default) or just the majors. */
export type VolatilityUniverse = "liquid" | "majors";

/** One currency's volatility over the window (annualized % of daily returns). */
export interface VolatilityResult {
  currency: CurrencyCode;
  /** Annualized volatility in percent (stdev of daily returns × √365 × 100); null if too little data. */
  volatility: number | null;
  /** Number of daily returns the figure was computed from. */
  observations: number;
  /** Why volatility is null (e.g. not enough history in the window). */
  note?: string;
}

/**
 * GET /v1/volatility — annualized volatility of one or more currencies vs `base`,
 * or a stability ranking when no currency is named. Growth+ only.
 */
export interface VolatilityResponse {
  base: CurrencyCode;
  from: IsoDate;
  to: IsoDate;
  /** `single`/`compare` for named currencies; `ranking` when ranking a universe. */
  mode: "compare" | "ranking";
  /** Ranking direction (ranking mode only). */
  sort?: VolatilitySort;
  /** The universe ranked (ranking mode only): `liquid` (default) or `majors`. */
  universe?: VolatilityUniverse;
  /** Annualized from daily (calendar) returns — documented basis for the figure. */
  basis: "annualized-daily";
  results: VolatilityResult[];
}

/** One currency's return-correlation with the base over the window. */
export interface CorrelationResult {
  currency: CurrencyCode;
  /** Pearson correlation of daily returns vs `base` (−1..1); null if undefined. */
  correlation: number | null;
  observations: number;
  note?: string;
}

/**
 * GET /v1/correlation — correlation of each compared currency's daily returns
 * with the `base` currency's, over the window. Growth+ only.
 */
export interface CorrelationResponse {
  base: CurrencyCode;
  from: IsoDate;
  to: IsoDate;
  results: CorrelationResult[];
}

/** One currency's worst peak-to-trough decline vs base over the window. */
export interface DrawdownResult {
  currency: CurrencyCode;
  /** Worst peak-to-trough move in the quoted rate, as a signed percent; null if none. */
  maxDrawdownPct: number | null;
  peakDate?: IsoDate;
  troughDate?: IsoDate;
  /** First date the rate recovered to the prior peak, or null if it hadn't by `to`. */
  recoveryDate?: IsoDate | null;
  /** Calendar days from trough to recovery; null if it hadn't recovered by `to`. */
  recoveryDays?: number | null;
  note?: string;
}

/** Drawdown RANKING direction: `deepest` (largest decline first) or `recovery` (fastest recovery first). */
export type DrawdownSort = "deepest" | "recovery";

/**
 * GET /v1/drawdown — maximum drawdown of one or more currencies vs `base` over
 * the window, with peak/trough/recovery dates. Name `currency`/`currencies` to
 * measure them, or omit to RANK the liquid set by drawdown. Growth+ only.
 */
export interface DrawdownResponse {
  base: CurrencyCode;
  from: IsoDate;
  to: IsoDate;
  /** `compare` for named currencies; `ranking` when ranking a universe. */
  mode: "compare" | "ranking";
  /** Ranking direction (ranking mode only). */
  sort?: DrawdownSort;
  /** Universe ranked (ranking mode only): `liquid` (default) or `majors`. */
  universe?: VolatilityUniverse;
  results: DrawdownResult[];
}

// --- Safe-haven analytics ---------------------------------------------------

/** One currency's safe-haven profile + composite score (0-100, higher = safer). */
export interface SafeHavenResult {
  currency: CurrencyCode;
  /** Composite safe-haven score 0-100 (low volatility + shallow drawdown + crisis resilience). */
  score: number | null;
  /** Annualized volatility (%) over the window. */
  volatility: number | null;
  /** Worst peak-to-trough decline (% , signed) over the window. */
  maxDrawdownPct: number | null;
  /** % the currency appreciated vs base during the reference crisis window (positive = held value). */
  crisisReturnPct: number | null;
  note?: string;
}

/**
 * GET /v1/safe-haven — ranks currencies by a composite "safe-haven" score built
 * from low volatility, shallow max drawdown, and resilience during a reference
 * crisis (the 2008 global financial crisis by default). Growth+ only.
 */
export interface SafeHavenResponse {
  base: CurrencyCode;
  from: IsoDate;
  to: IsoDate;
  /** The crisis window the `crisisReturnPct` is measured over. */
  crisis: { label: string; from: IsoDate; to: IsoDate };
  universe?: VolatilityUniverse;
  results: SafeHavenResult[];
}

/** One currency's mean-reversion profile + score (0-100, higher = more mean-reverting). */
export interface MeanReversionResult {
  currency: CurrencyCode;
  /** Composite 0-100: how strongly + fast the rate returns to its window mean. */
  score: number | null;
  /** Current deviation from the window mean, signed percent. */
  deviationPct: number | null;
  /** Mean crossings per observation (0..1) — higher = oscillates around fair value more. */
  reversionFrequency: number | null;
  /** Estimated half-life of a deviation, in calendar days (AR(1)); null when non-reverting. */
  halfLifeDays: number | null;
  note?: string;
}

/**
 * GET /v1/mean-reversion — ranks currencies by how strongly they revert to their
 * mean ("fair value") over the window. Name `currencies` to score a set, or omit
 * to rank the liquid set. Growth+ only.
 */
export interface MeanReversionResponse {
  base: CurrencyCode;
  from: IsoDate;
  to: IsoDate;
  universe?: VolatilityUniverse;
  results: MeanReversionResult[];
}

export interface Country {
  /** ISO/IMF alpha-3 code, e.g. "USA", "IND". */
  code: CountryCode;
  name: string;
  /** Official ISO 4217 currency codes for this country, e.g. ["INR"], ["EUR"]. */
  currencies?: string[];
}

/** GET /countries — the full reference list (public, no quota). */
export type CountriesResponse = Country[];

/** A reference currency (ISO 4217). */
export interface Currency {
  /** ISO 4217 alpha code, e.g. "USD", "INR". */
  code: CurrencyCode;
  /** ISO 4217 numeric code, zero-padded, e.g. "840", "356". */
  numeric: string;
  name: string;
  /** Currency symbol, e.g. "$", "₹". */
  symbol: string;
}

/** GET /currencies — the full reference list of supported currencies (public, no quota). */
export type CurrenciesResponse = Currency[];

export interface HealthResponse {
  status: "ok" | "degraded";
  service: "currencycore-api";
  time: string;
  checks: {
    kv: boolean;
    d1: boolean;
  };
  latestRateDate?: IsoDate;
}

// ---------------------------------------------------------------------------
// Status page / incidents
// ---------------------------------------------------------------------------

export type IncidentImpact = "minor" | "major" | "critical";
export type IncidentStatus = "investigating" | "identified" | "monitoring" | "resolved";
/** Overall reported status on the public status page. */
export type SystemStatus = "operational" | "degraded" | "outage";

export interface IncidentUpdateDTO {
  status: IncidentStatus;
  body: string;
  createdAt: string;
}

export interface IncidentDTO {
  id: string;
  title: string;
  impact: IncidentImpact;
  status: IncidentStatus;
  /** True when opened by the automated monitor (vs an admin). */
  auto: boolean;
  startedAt: string;
  resolvedAt: string | null;
  updates: IncidentUpdateDTO[];
}

/** One day in the 90-day uptime strip. `nodata` = no checks were recorded that day. */
export interface UptimeDay {
  date: IsoDate;
  status: SystemStatus | "nodata";
  /** Percentage of checks that passed that day (null when nodata). */
  uptimePct: number | null;
}

/** Public GET /status response — powers the status page (no auth, cacheable). */
export interface StatusResponse {
  status: SystemStatus;
  /** ISO timestamp of this reading. */
  updatedAt: string;
  uptime: { pct90: number; days: UptimeDay[] };
  /** Active incidents + those started within the last 90 days, newest first. */
  incidents: IncidentDTO[];
}

// ---------------------------------------------------------------------------
// BYOK — bring your own AI provider keys (Growth+). The org points /ai at its
// own provider; external-provider calls count toward the monthly API request
// quota, our providers consume AI credits. One metadata list drives both the
// dashboard forms and the server-side provider resolution.
// ---------------------------------------------------------------------------

export type ByokProviderId =
  | "openai"
  | "anthropic"
  | "google"
  | "azure"
  | "openrouter"
  | "groq"
  | "deepseek"
  | "mistral"
  | "custom";

export interface ByokProviderMeta {
  id: ByokProviderId;
  label: string;
  /** Default API base; "" when the org must supply one (azure/custom). */
  defaultBase: string;
  /** Request shape: openai = OpenAI-compatible /chat/completions; others use bespoke adapters. */
  api: "openai" | "anthropic" | "google";
  /** Auth header for the OpenAI-compatible providers (bearer vs Azure's api-key). */
  authStyle: "bearer" | "api-key";
  needsBaseUrl: boolean;
  needsApiVersion: boolean;
  /** Example model id for the form placeholder. */
  modelExample: string;
}

export const BYOK_PROVIDERS: readonly ByokProviderMeta[] = [
  { id: "openai", label: "OpenAI", defaultBase: "https://api.openai.com/v1", api: "openai", authStyle: "bearer", needsBaseUrl: false, needsApiVersion: false, modelExample: "gpt-4o-mini" },
  { id: "anthropic", label: "Anthropic", defaultBase: "https://api.anthropic.com", api: "anthropic", authStyle: "api-key", needsBaseUrl: false, needsApiVersion: false, modelExample: "claude-haiku-4-5" },
  { id: "google", label: "Google Gemini", defaultBase: "https://generativelanguage.googleapis.com", api: "google", authStyle: "api-key", needsBaseUrl: false, needsApiVersion: false, modelExample: "gemini-2.0-flash" },
  { id: "azure", label: "Azure OpenAI", defaultBase: "", api: "openai", authStyle: "api-key", needsBaseUrl: true, needsApiVersion: true, modelExample: "gpt-4o-mini" },
  { id: "openrouter", label: "OpenRouter", defaultBase: "https://openrouter.ai/api/v1", api: "openai", authStyle: "bearer", needsBaseUrl: false, needsApiVersion: false, modelExample: "openai/gpt-4o-mini" },
  { id: "groq", label: "Groq", defaultBase: "https://api.groq.com/openai/v1", api: "openai", authStyle: "bearer", needsBaseUrl: false, needsApiVersion: false, modelExample: "llama-3.3-70b-versatile" },
  { id: "deepseek", label: "DeepSeek", defaultBase: "https://api.deepseek.com/v1", api: "openai", authStyle: "bearer", needsBaseUrl: false, needsApiVersion: false, modelExample: "deepseek-chat" },
  { id: "mistral", label: "Mistral", defaultBase: "https://api.mistral.ai/v1", api: "openai", authStyle: "bearer", needsBaseUrl: false, needsApiVersion: false, modelExample: "mistral-large-latest" },
  { id: "custom", label: "Custom (OpenAI-compatible)", defaultBase: "", api: "openai", authStyle: "bearer", needsBaseUrl: true, needsApiVersion: false, modelExample: "your-model-id" },
];

/** Look up a BYOK provider's metadata, or null for an unknown id. */
export function byokProviderMeta(id: string): ByokProviderMeta | null {
  return BYOK_PROVIDERS.find((p) => p.id === id) ?? null;
}

/** A configured BYOK provider as returned to the client — never includes the key. */
export interface ByokProviderRecord {
  id: string;
  provider: ByokProviderId;
  label: string | null;
  baseUrl: string | null;
  apiVersion: string | null;
  model: string;
  priority: number;
  active: boolean;
  /** Last test-connection outcome: true ok, false failed, null untested. */
  lastTestOk: boolean | null;
  lastTestedAt: string | null;
  /**
   * Whether the stored key currently decrypts into a usable provider (server-set
   * on GET). `false` means the key can't be read — usually because BYOK_ENC_KEY
   * was rotated after the key was saved; re-enter the key to fix it. Undefined
   * when not computed (create/update responses).
   */
  keyOk?: boolean;
}

/** Org BYOK settings + its configured providers (GET /orgs/:id/byok). */
export interface ByokSettings {
  /** Whether BYOK is enabled (master switch). */
  enabled: boolean;
  /** Fall back to CurrencyCore's providers (consuming an AI credit) when the org's provider fails. */
  fallback: boolean;
  providers: ByokProviderRecord[];
  /** Whether the org's plan allows BYOK (Growth+). */
  available: boolean;
  /** Whether the platform is configured for BYOK (the encryption key is set). */
  configured: boolean;
  /** Max tool calls per /ai request on the org's own provider; null = unlimited. Default 10. */
  maxToolCalls: number | null;
}

/** Create/update a BYOK provider. `apiKey` omitted on update keeps the stored key. */
export interface ByokProviderWrite {
  provider: ByokProviderId;
  label?: string | null;
  apiKey?: string;
  baseUrl?: string | null;
  apiVersion?: string | null;
  model: string;
  active?: boolean;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export const ERROR_CODES = {
  INVALID_INPUT: "invalid_input",
  UNSUPPORTED_QUERY: "unsupported_query",
  MISSING_PPP_COUNTRIES: "missing_ppp_countries",
  UNKNOWN_CURRENCY: "unknown_currency",
  UNKNOWN_PPP: "unknown_ppp",
  RATE_NOT_FOUND: "rate_not_found",
  UNAUTHORIZED: "unauthorized",
  FORBIDDEN: "forbidden",
  RATE_LIMITED: "rate_limited",
  FREE_TIER_ORG_LIMIT: "free_tier_org_limit",
  INTERNAL: "internal_error",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export interface ApiError {
  error: {
    code: ErrorCode;
    message: string;
    details?: unknown;
  };
}

// ---------------------------------------------------------------------------
// Plans / billing
// ---------------------------------------------------------------------------

export type PlanTier = "free" | "starter" | "growth" | "growth_plus" | "business" | "enterprise";

export interface PlanDefinition {
  tier: PlanTier;
  label: string;
  /** Monthly API request quota. Infinity for enterprise (custom/unlimited). */
  monthlyRequests: number;
  /**
   * Per-minute request rate limit (60s window), mirroring the DB `plans.rate_limit`.
   * Fixed tiers are enforced by the native RATE_<TIER> Cloudflare binding. Enterprise
   * is `null` = bespoke: the limit is admin-configured per deal (plan/per-org) and
   * shown as "Custom", not a fixed catalog number.
   */
  rateLimit: number | null;
  /**
   * Monthly AI credit allocation — a SEPARATE hard pool, independent of the API
   * request quota. null = unlimited/custom (enterprise). Consumed first, before
   * the org's never-expiring purchased balance. 1 AI request = AI_CREDITS_PER_REQUEST.
   */
  aiCreditLimit: number | null;
  /** Team seat limit (members + pending invites). null = unlimited. */
  seatLimit: number | null;
  /** USD/month. -1 means "contact us" (enterprise, starting at $499). */
  priceUsd: number;
  /** MCP access — included on every plan. */
  mcpEnabled: boolean;
  /** OIDC SSO entitlement (Business+). */
  ssoEnabled: boolean;
  /** Customer audit-logs entitlement (Business+). */
  auditLogsEnabled: boolean;
  /**
   * Audit-log retention window in days. Entitled tiers keep a long window
   * (Business ~24mo, Enterprise ~36mo); non-entitled tiers keep a short grace
   * window so an upgrade reveals recent history without unbounded storage.
   * Admin-overridable per org (`org_subscriptions.audit_retention_days`).
   */
  auditRetentionDays: number;
  /** Priority support entitlement (Growth+). */
  prioritySupportEnabled: boolean;
  /** Custom SLA entitlement (Enterprise). */
  customSlaEnabled: boolean;
  /** Historical time-series API (/history) entitlement (Growth+). */
  historyApiEnabled: boolean;
  /**
   * Over-usage rate in cents per 1,000 API requests above the monthly limit.
   * null = no metered overage on this tier: Free hard-caps at its limit, and
   * Enterprise overage is admin-managed (custom). The DB `plans.overage_cents` /
   * `overage_unit` are kept in sync with this and the Dodo meter price.
   */
  overagePer1000Cents: number | null;
  /**
   * How many days of historical FX data the plan may query (`?date=`).
   * null = full history. Free is limited to the last 30 days.
   */
  historicalDays: number | null;
  /** Dodo Payments product id (set via env in production). */
  dodoProductIdEnvVar: string;
}

export const PLAN_LIMITS: Record<PlanTier, PlanDefinition> = {
  free: {
    tier: "free",
    label: "Free",
    monthlyRequests: 1_000,
    rateLimit: 60,
    aiCreditLimit: 100,
    seatLimit: 3,
    priceUsd: 0,
    mcpEnabled: true,
    ssoEnabled: false,
    auditLogsEnabled: false,
    auditRetentionDays: 90,
    prioritySupportEnabled: false,
    customSlaEnabled: false,
    overagePer1000Cents: null,
    historicalDays: 30,
    historyApiEnabled: false,
    dodoProductIdEnvVar: "",
  },
  starter: {
    tier: "starter",
    label: "Starter",
    monthlyRequests: 50_000,
    rateLimit: 120,
    aiCreditLimit: 1_000,
    seatLimit: 10,
    priceUsd: 9,
    mcpEnabled: true,
    ssoEnabled: false,
    auditLogsEnabled: false,
    auditRetentionDays: 90,
    prioritySupportEnabled: false,
    customSlaEnabled: false,
    overagePer1000Cents: 5,
    historicalDays: null,
    historyApiEnabled: false,
    dodoProductIdEnvVar: "DODO_PRODUCT_STARTER",
  },
  growth: {
    tier: "growth",
    label: "Growth",
    monthlyRequests: 500_000,
    rateLimit: 600,
    aiCreditLimit: 5_000,
    seatLimit: null,
    priceUsd: 29,
    mcpEnabled: true,
    ssoEnabled: false,
    auditLogsEnabled: false,
    auditRetentionDays: 90,
    prioritySupportEnabled: true,
    customSlaEnabled: false,
    overagePer1000Cents: 4,
    historicalDays: null,
    historyApiEnabled: true,
    dodoProductIdEnvVar: "DODO_PRODUCT_GROWTH",
  },
  growth_plus: {
    tier: "growth_plus",
    label: "Growth Plus",
    monthlyRequests: 2_000_000,
    rateLimit: 3000,
    aiCreditLimit: 20_000,
    seatLimit: null,
    priceUsd: 79,
    mcpEnabled: true,
    ssoEnabled: false,
    auditLogsEnabled: false,
    auditRetentionDays: 90,
    prioritySupportEnabled: true,
    customSlaEnabled: false,
    overagePer1000Cents: 4,
    historicalDays: null,
    historyApiEnabled: true,
    dodoProductIdEnvVar: "DODO_PRODUCT_GROWTH_PLUS",
  },
  business: {
    tier: "business",
    label: "Business",
    monthlyRequests: 10_000_000,
    rateLimit: 6000,
    aiCreditLimit: 50_000,
    seatLimit: null,
    priceUsd: 199,
    mcpEnabled: true,
    ssoEnabled: true,
    auditLogsEnabled: true,
    auditRetentionDays: 730,
    prioritySupportEnabled: true,
    customSlaEnabled: false,
    overagePer1000Cents: 3,
    historicalDays: null,
    historyApiEnabled: true,
    dodoProductIdEnvVar: "DODO_PRODUCT_BUSINESS",
  },
  enterprise: {
    tier: "enterprise",
    label: "Enterprise",
    monthlyRequests: Number.POSITIVE_INFINITY,
    rateLimit: null,
    aiCreditLimit: null,
    seatLimit: null,
    priceUsd: -1,
    mcpEnabled: true,
    ssoEnabled: true,
    auditLogsEnabled: true,
    auditRetentionDays: 1095,
    prioritySupportEnabled: true,
    customSlaEnabled: true,
    overagePer1000Cents: null,
    historicalDays: null,
    historyApiEnabled: true,
    dodoProductIdEnvVar: "DODO_PRODUCT_ENTERPRISE",
  },
};

/**
 * Whether a plan id is entitled to the historical time-series API (`/history`).
 * Growth+ (growth, growth_plus, business, enterprise); free + starter are not.
 * Custom/unknown ids are treated as top-tier (entitled), matching how the
 * runtime feature gates handle bespoke plans. The D1 `plans.history_api_enabled`
 * column (admin-editable) is the authoritative override at request time; this
 * pure helper is the static default used by the dashboard, pricing, and docs.
 */
export function planHasHistoryApi(planId: string): boolean {
  return planId in PLAN_LIMITS ? PLAN_LIMITS[planId as PlanTier].historyApiEnabled : true;
}

/**
 * Whether a plan id may bring its own AI provider keys (BYOK) for the `/ai`
 * endpoint. Growth+ (growth, growth_plus, business, enterprise); free + starter
 * are not. Custom/unknown ids are treated as top-tier (entitled), matching the
 * other gates. BYOK shares the Growth+ entitlement boundary with the history API.
 */
export function planHasByok(planId: string): boolean {
  return planHasHistoryApi(planId);
}

/**
 * DB-level plan value: the six billable tiers plus the hidden, admin-only
 * "custom" plan. `custom` is NEVER offered in the dashboard billing UI and has
 * no Dodo product — its limits come from per-org override columns on
 * `org_subscriptions` (see CustomPlanOverrides), not from PLAN_LIMITS.
 */
export type OrgPlan = PlanTier | "custom";

/** Plans a normal user can self-select in the dashboard billing UI. */
export const SELECTABLE_PLANS: PlanTier[] = [
  "free",
  "starter",
  "growth",
  "growth_plus",
  "business",
  "enterprise",
];

/** Plans an admin can assign to an org (includes the hidden Custom plan). */
export const ADMIN_PLANS: OrgPlan[] = [...SELECTABLE_PLANS, "custom"];

export const CUSTOM_PLAN_LABEL = "Custom";

/**
 * AI credit add-on packages. One-time purchases (NOT a subscription) billed
 * through Dodo. Purchased credits never expire and are consumed only after the
 * monthly allocation is exhausted.
 */
export interface AiCreditPackage {
  /** Stable id, also the DB `ai_credit_packages.id`. */
  id: string;
  credits: number;
  /** Price in USD cents. */
  priceCents: number;
  /** Env var holding the Dodo one-time product id (set in production). */
  dodoProductIdEnvVar: string;
}

export const AI_CREDIT_PACKAGES: AiCreditPackage[] = [
  { id: "credits_10k", credits: 10_000, priceCents: 1_900, dodoProductIdEnvVar: "DODO_PRODUCT_CREDITS_10K" },
  { id: "credits_25k", credits: 25_000, priceCents: 4_900, dodoProductIdEnvVar: "DODO_PRODUCT_CREDITS_25K" },
  { id: "credits_50k", credits: 50_000, priceCents: 8_900, dodoProductIdEnvVar: "DODO_PRODUCT_CREDITS_50K" },
  { id: "credits_100k", credits: 100_000, priceCents: 16_900, dodoProductIdEnvVar: "DODO_PRODUCT_CREDITS_100K" },
];

/**
 * Admin/DB view of an AI credit package (`ai_credit_packages` row). Unlike the
 * static {@link AiCreditPackage} catalog, the Dodo product id lives on the row
 * and is editable from the admin Plans page; the purchase flow prefers it and
 * falls back to the matching `dodoProductIdEnvVar` when it's blank.
 */
export interface AiCreditPackageRecord {
  id: string;
  credits: number;
  /** Price in USD cents. */
  priceCents: number;
  /** Dodo one-time product id for checkout. null until configured. */
  dodoProductId: string | null;
  /** Inactive packages are hidden from the customer purchase UI. */
  active: boolean;
  /** Display order (ascending). */
  sort: number;
}

/** Admin edit of an AI credit package (upsert). */
export interface AiCreditPackageWrite {
  credits: number;
  priceCents: number;
  dodoProductId?: string | null;
  active?: boolean;
  sort?: number;
}

/** Credits consumed per AI request. Isolated here so token/per-model costing can replace it. */
export const AI_CREDITS_PER_REQUEST = 1;

/** Per-org overrides that define the hidden Custom plan's effective limits. */
export interface CustomPlanOverrides {
  /** Effective monthly request quota. null = fall back to the Free tier. */
  monthlyRequests: number | null;
  /** Effective per-window rate limit. null = fall back to the Free tier. */
  rateLimit: number | null;
  /** Optional display label for the custom plan. */
  label: string | null;
}

/** Human label for any org plan, including the hidden Custom plan. */
export function planLabel(plan: OrgPlan, customLabel?: string | null): string {
  if (plan === "custom") return customLabel?.trim() || CUSTOM_PLAN_LABEL;
  return PLAN_LIMITS[plan].label;
}

// ---------------------------------------------------------------------------
// Organizations / usage (multi-tenant)
// ---------------------------------------------------------------------------

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  plan: PlanTier;
  /** Current user's role within the org. */
  role: "owner" | "admin" | "member";
}

export interface UsageStats {
  organizationId: string;
  /** YYYY-MM billing period. */
  period: string;
  requests: number;
  /** Base monthly quota (null = unlimited). */
  limit: number | null;
  /** Effective quota incl. the active grant (null = unlimited). */
  effectiveLimit: number | null;
  /** Extra requests from an active compensation grant (0 if none). */
  grantRequests: number;
  plan: OrgPlan;
  planLabel: string;
  planKind: PlanKind;
  /** Org's own hard cap (null = off). */
  hardCapRequests: number | null;
  /** Admin ceiling (null = off). */
  adminHardCapRequests: number | null;
  /** Lowest set cap (null = no cap). */
  effectiveCap: number | null;
  /** Monthly AI credit allocation (null = unlimited/custom). */
  aiCreditLimit: number | null;
  /** AI credits drawn from the monthly allocation this period. */
  aiCreditsUsed: number;
  /** Monthly AI credits left (null = unlimited). */
  aiCreditsRemaining: number | null;
  /** Never-expiring purchased AI credit balance. */
  purchasedAiCredits: number;
  /** Team seat limit (null = unlimited). */
  seatLimit: number | null;
  /** Seats used = active members + pending invitations. */
  seatsUsed: number;
  /** Billing cycle window (from the subscription / Dodo). null until set. */
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  /** The requesting member's role, so the UI can gate editing. */
  viewerRole?: OrgRole;
}

/** The key-gated endpoints whose per-key usage is tracked (`usage_key_monthly`). */
export type UsageEndpoint = "convert" | "rates" | "ppp" | "ai";

/** Request counts per tracked endpoint. */
export type EndpointCounts = Record<UsageEndpoint, number>;

/** Input / output / total tokens — the shape every admin token card renders. */
export interface TokenCounts {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/** One API key's monthly usage (GET /orgs/:orgId/usage/keys). Requests only — no tokens. */
export interface KeyUsage {
  keyId: string;
  /** Key name (or null when the key was deleted). */
  name: string | null;
  environment: string; // 'test' | 'live'
  requests: number;
  byEndpoint: EndpointCounts;
}

/** Per-key monthly usage for one org. */
export interface KeyUsageResponse {
  organizationId: string;
  period: string; // YYYY-MM
  keys: KeyUsage[];
  /** Org-wide per-endpoint totals for the month (sum across keys). */
  byEndpoint: EndpointCounts;
}

/** Per-org token usage for the admin app (a token card = input/output/total). */
export interface OrgTokenUsage {
  organizationId: string;
  name: string;
  total: TokenCounts; // lifetime "till now" (incl. the in-progress month)
  month: TokenCounts; // current period
  avgMonthly: TokenCounts; // total ÷ active months
  monthsActive: number;
}

/** One month of app-wide tokens (the admin monthly-tokens chart). */
export interface MonthlyTokenPoint {
  period: string; // YYYY-MM
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/** App-wide token + endpoint analytics for the admin usage page. */
export interface AppUsageAnalytics {
  totalTokens: TokenCounts; // till now
  monthTokens: TokenCounts; // current month
  avgMonthlyTokens: TokenCounts;
  monthsActive: number;
  endpointTotal: EndpointCounts; // requests till now
  endpointMonth: EndpointCounts; // requests this month
  monthly: MonthlyTokenPoint[]; // history for the chart (+ live current month)
  topOrgs: OrgTokenUsage[]; // top 10 by this month's tokens
}

/** Per-org monthly token series (admin org page chart). */
export interface OrgTokenSeries {
  organizationId: string;
  months: MonthlyTokenPoint[];
}

/** One day of an org's request counts (`usage_daily`), with the MCP-sourced subset. */
export interface UsageDay {
  /** YYYY-MM-DD (UTC). */
  date: string;
  requests: number;
  /** Subset of `requests` that came through the MCP server. */
  mcpRequests: number;
  /** Subset of `requests` that came through the AI endpoint. */
  aiRequests: number;
}

/** A dense, chronological daily-usage window for one org. */
export interface UsageSeries {
  organizationId: string;
  /** Chronological, gap-filled; length equals the requested day window. */
  days: UsageDay[];
}

/** A currency conversion produced by the AI endpoint's `convert` tool. */
export interface AiConversionResult {
  tool: "convert";
  from: string;
  to: string;
  amount: number;
  result: number;
  rate: number;
  pppApplied: boolean;
  asOf: string;
  /** When PPP was applied, the alpha-3 countries used (resolved/defaulted from the
   *  currencies when not given) — so the answer can say which countries it assumed. */
  fromCountry?: string;
  toCountry?: string;
  /** When PPP was applied, the year of the PPP factors used. */
  pppYear?: number;
  /** When PPP was applied, the source of the PPP factors (e.g. "IMF World Economic Outlook"). */
  pppSource?: string;
}

/** A rate lookup produced by the AI endpoint's `rates` tool. */
export interface AiRateResult {
  tool: "rates";
  base: string;
  asOf: string;
  rates: Record<string, number>;
}

/**
 * A currency resolution produced by the AI endpoint's `resolve_currency` tool:
 * the ISO 4217 matches for a free-text currency name, symbol, or country, best
 * match first. Lets the model confirm the exact code before convert/get_rates.
 */
export interface AiCurrencyResolveResult {
  tool: "resolve_currency";
  query: string;
  matches: Currency[];
  /** Per matched currency: the alpha-3 countries that use it + a default (for PPP).
   *  Lets the model pick the default, or ask the user when several countries share
   *  the currency (e.g. EUR). Omitted when no match has country data. */
  countries?: Record<string, { countries: string[]; default: string | null }>;
}

/**
 * A historical-analysis result from the AI endpoint's `analyze_history` tool
 * (Growth+). Mirrors GET /v1/history/analysis, trimmed to what the model needs to
 * write its answer — and ALSO surfaced in the /ai response `data` so callers can
 * use the structured numbers in code.
 */
export interface AiHistoryAnalysisResult {
  tool: "history_analysis";
  base: string;
  from: string;
  to: string;
  mode: "compare" | "movers";
  results: {
    currency: string;
    startRate: number | null;
    endRate: number | null;
    changePct: number | null;
    /** Positive = the currency appreciated vs base over the window. */
    strengthenedPct: number | null;
    stats?: { min: number; max: number; avg: number };
    /** Downsampled trend (period → avg rate) — interval auto-picked by span unless set. */
    series?: Record<string, number>;
    /** Annualized volatility (%) over the window — present in compare mode (for reports). */
    volatility?: number | null;
  }[];
}

/**
 * A PPP-over-time result from the AI endpoint's `analyze_ppp` tool (Growth+).
 * Mirrors GET /v1/ppp/analysis, trimmed to what the model needs to write its
 * answer — and ALSO surfaced in the /ai response `data` so callers can use the
 * structured numbers in code. The PPP factor is local units per international $.
 */
export interface AiPppAnalysisResult {
  tool: "ppp_analysis";
  from: number;
  to: number;
  mode: "compare" | "movers";
  results: {
    country: string;
    startYear: number;
    endYear: number;
    startFactor: number | null;
    endFactor: number | null;
    /** % change in the PPP factor over the window (positive = factor rose). */
    changePct: number | null;
    /** True when the end value is an IMF projection, not actual. */
    projected: boolean;
    stats?: { min: number; max: number; avg: number };
    /** Year → factor trend (compare mode). */
    series?: Record<string, number>;
  }[];
}

/**
 * A volatility result from the AI endpoint's `analyze_volatility` tool (Growth+).
 * Mirrors GET /v1/volatility, trimmed to what the model needs + surfaced in `data`.
 */
export interface AiVolatilityResult {
  tool: "volatility";
  base: string;
  from: string;
  to: string;
  mode: "compare" | "ranking";
  results: { currency: string; volatility: number | null; observations: number }[];
}

/** A correlation result from the AI endpoint's `analyze_correlation` tool (Growth+). */
export interface AiCorrelationResult {
  tool: "correlation";
  base: string;
  from: string;
  to: string;
  results: { currency: string; correlation: number | null; observations: number }[];
}

/** A drawdown result from the AI endpoint's `analyze_drawdown` tool (Growth+). */
export interface AiDrawdownResult {
  tool: "drawdown";
  base: string;
  from: string;
  to: string;
  results: {
    currency: string;
    maxDrawdownPct: number | null;
    peakDate?: string;
    troughDate?: string;
    recoveryDate?: string | null;
    recoveryDays?: number | null;
  }[];
}

/** A safe-haven result from the AI endpoint's `analyze_safe_haven` tool (Growth+). */
export interface AiSafeHavenResult {
  tool: "safe_haven";
  base: string;
  from: string;
  to: string;
  /** The reference crisis label the crisisReturnPct is measured over. */
  crisis: string;
  results: {
    currency: string;
    score: number | null;
    volatility: number | null;
    maxDrawdownPct: number | null;
    crisisReturnPct: number | null;
  }[];
}

/** A mean-reversion result from the AI endpoint's `analyze_mean_reversion` tool (Growth+). */
export interface AiMeanReversionResult {
  tool: "mean_reversion";
  base: string;
  from: string;
  to: string;
  results: {
    currency: string;
    score: number | null;
    deviationPct: number | null;
    reversionFrequency: number | null;
    halfLifeDays: number | null;
  }[];
}

/**
 * A risk-adjusted-return result from the AI endpoint's `analyze_risk_adjusted`
 * tool (Growth+): annualized return ÷ annualized volatility (Sharpe-like), per
 * currency vs base, ranked or for a named set.
 */
export interface AiRiskAdjustedResult {
  tool: "risk_adjusted";
  base: string;
  from: string;
  to: string;
  mode: "compare" | "ranking";
  results: {
    currency: string;
    /** Annualized appreciation vs base (%, positive = strengthened). */
    annualReturn: number | null;
    /** Annualized volatility (%). */
    volatility: number | null;
    /** annualReturn ÷ volatility. Higher = more return per unit of risk. */
    riskAdjusted: number | null;
  }[];
}

/**
 * A consecutive-strengthening-streak result from `analyze_streaks` (Growth+):
 * the longest run of consecutive calendar years a currency strengthened vs base.
 */
export interface AiStreaksResult {
  tool: "streaks";
  base: string;
  from: string;
  to: string;
  mode: "compare" | "ranking";
  results: {
    currency: string;
    /** Longest consecutive-years strengthening run. */
    maxStreakYears: number;
    /** Run length ending at the most recent year. */
    currentStreakYears: number;
    /** Years (inclusive) of the longest run, or null when there was none. */
    streakFrom: number | null;
    streakTo: number | null;
  }[];
}

/**
 * A stability-band result from `analyze_stability` (Growth+): how far each
 * currency strayed from its starting value, and whether it stayed within ±band%.
 */
export interface AiStabilityResult {
  tool: "stability";
  base: string;
  from: string;
  to: string;
  mode: "compare" | "ranking";
  /** The ± band (%) used to flag/rank stability (e.g. 5 for ±5%). */
  bandPct: number;
  results: {
    currency: string;
    /** Max absolute deviation from the window's first value (%). */
    maxDeviationPct: number | null;
    /** True when it never strayed beyond ±bandPct of its start. */
    withinBand: boolean;
    /** Net annualized change vs base (%) for context. */
    annualReturn: number | null;
  }[];
}

/** One structured result from a tool the AI model invoked. */
export type AiToolResult =
  | AiConversionResult
  | AiRateResult
  | AiCurrencyResolveResult
  | AiHistoryAnalysisResult
  | AiPppAnalysisResult
  | AiVolatilityResult
  | AiCorrelationResult
  | AiDrawdownResult
  | AiSafeHavenResult
  | AiMeanReversionResult
  | AiRiskAdjustedResult
  | AiStreaksResult
  | AiStabilityResult;

/**
 * A structured result surfaced in the /ai response `data`: the underlying
 * convert / rates / history-analysis data WITHOUT the internal `tool` marker. The
 * `resolve_currency` resolution step is an internal detail and is never included.
 */
export type AiResponseDatum =
  | Omit<AiConversionResult, "tool">
  | Omit<AiRateResult, "tool">
  | Omit<AiHistoryAnalysisResult, "tool">
  | Omit<AiPppAnalysisResult, "tool">
  | Omit<AiVolatilityResult, "tool">
  | Omit<AiCorrelationResult, "tool">
  | Omit<AiDrawdownResult, "tool">
  | Omit<AiSafeHavenResult, "tool">
  | Omit<AiMeanReversionResult, "tool">
  | Omit<AiRiskAdjustedResult, "tool">
  | Omit<AiStreaksResult, "tool">
  | Omit<AiStabilityResult, "tool">;

/** Response from GET /v1/ai — the natural-language answer plus its result data. */
export interface AiResponse {
  query: string;
  answer: string;
  /** The convert/rate result(s) behind the answer (no tool/process metadata). */
  data: AiResponseDatum[];
  /**
   * Number of tool calls the engine ran to produce this answer (convert, rates,
   * analysis — the currency-resolution step is not counted). With BYOK active you
   * are billed one API request per tool call; on CurrencyCore's models it's one
   * AI credit regardless of the count.
   */
  toolCalls?: number;
  /**
   * Present only when the org has BYOK active: `true` if this answer came from
   * the org's own provider (metered as an API request), `false` if it came from
   * CurrencyCore's models (a fallback, metered as an AI credit). Omitted entirely
   * for orgs not using BYOK.
   */
  byok?: boolean;
}

/** Per-org AI endpoint status + usage (GET /orgs/:orgId/ai). */
export interface AiStatus {
  enabled: boolean;
  /** YYYY-MM billing period. */
  period: string;
  /** Total API requests this period (excludes AI — AI is metered separately). */
  requests: number;
  /** AI-sourced requests this period. */
  aiRequests: number;
  /** Monthly AI credit allocation; null = unlimited/custom (enterprise). */
  aiLimit: number | null;
  /** AI credits drawn from the monthly allocation this period. */
  aiCreditsUsed: number;
  /** Monthly AI credits left; null = unlimited. */
  aiCreditsRemaining: number | null;
  /** Never-expiring purchased AI credit balance (add-ons / admin grants). */
  purchasedAiCredits: number;
  /** Whether the AI endpoint is enabled platform-wide (admin kill switch). */
  globalEnabled: boolean;
  /** Admin-set message shown when AI is unavailable (global or org disabled). */
  disabledMessage: string | null;
}

/** Platform-wide AI endpoint settings (admin app). */
export interface AiGlobalSettings {
  enabled: boolean;
  /** Custom message shown to users alongside the "AI disabled" notice. */
  message: string;
}

/**
 * Live keys use the org's plan rate limit; test keys always use the Hobby tier.
 * Demo keys are sandbox-only (the in-dashboard playground): one per user, with
 * their own fixed budget that never counts against the org's real quota/bill.
 */
export type ApiKeyEnvironment = "live" | "test" | "demo";

/**
 * Demo (playground) API keys are sandbox-only: each user gets one per org, bound
 * to their signed-in session + the app origin, and never metered into the org's
 * `usage_counters` (so demo traffic never inflates real usage or billing).
 *
 * They are rate-limited (not lifetime-capped): {@link DEMO_KEY_RATE_PER_MINUTE}
 * requests per minute, enforced by Cloudflare's native rate-limit binding (zero
 * KV writes). This is the display/fallback value — the deployed `RATE_DEMO`
 * binding's `simple.limit` (per 60s) should match it.
 */
export const DEMO_KEY_RATE_PER_MINUTE = 30;

export interface ApiKeySummary {
  id: string;
  name: string;
  /** Masked, e.g. "cc_live_••••abcd". Full key only shown once at creation. */
  maskedKey: string;
  organizationId: string;
  environment: ApiKeyEnvironment;
  createdAt: string;
  lastUsedAt: string | null;
  enabled: boolean;
  /** Whether the full key can be revealed/copied later. */
  revealable?: boolean;
}

// ---------------------------------------------------------------------------
// Plan catalog (admin-managed `plans` table)
// ---------------------------------------------------------------------------

export type PlanInterval = "weekly" | "monthly" | "quarterly" | "half_yearly" | "annual";

/**
 * Over-usage (overage) billing state for the billing page (GET /orgs/:id/overage).
 * `pendingCents` is this cycle's overage computed from metered usage (the figure
 * reported to Dodo); `owedCents` is the carried balance for multi-month plans.
 * `billedHow`: "renewal" = added to the next invoice (weekly/monthly plans, settled
 * by Dodo's meter); "monthly" = charged on-demand each month (quarterly/annual).
 */
export interface OverageInfo {
  /** The plan has a metered overage rate (a paid standard tier). */
  available: boolean;
  /** This cycle's overage so far, in cents (0 when within the included limit). */
  pendingCents: number;
  /** Carried, not-yet-charged overage balance (multi-month plans), in cents. */
  owedCents: number;
  /** Per-unit overage rate in cents; null if the plan has no metered overage. */
  rateCents: number | null;
  /** Requests per billable unit (e.g. 1,000). */
  unit: number;
  interval: PlanInterval | null;
  /** How the overage is collected. */
  billedHow: "renewal" | "monthly";
  /** Next billing/charge date (ISO), authoritative from Dodo when available. */
  nextBillingDate: string | null;
  /** Next renewal base amount (excludes overage + tax), cents; from Dodo. */
  nextBillingBaseCents: number | null;
  currency: string;
  /** Requests used + the effective limit this period (null limit = unlimited). */
  requests: number;
  limit: number | null;
}

export const INTERVALS: PlanInterval[] = ["weekly", "monthly", "quarterly", "half_yearly", "annual"];

export function intervalLabel(i: PlanInterval): string {
  return {
    weekly: "Weekly",
    monthly: "Monthly",
    quarterly: "Quarterly",
    half_yearly: "Half-yearly",
    annual: "Annual",
  }[i];
}

export type PlanKind = "standard" | "custom";

/** A monthly_requests of null OR 0 (or negative) both mean unlimited. */
export function isUnlimited(monthlyRequests: number | null | undefined): boolean {
  return monthlyRequests == null || monthlyRequests <= 0;
}

/** Per-interval price in integer cents. */
export type PlanPrices = Partial<Record<PlanInterval, number>>;

/** URL-friendly handle from a display name: lowercased, non-alphanumerics → hyphens, trimmed. */
export function slugify(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

/** Intervals that at least one plan actually prices (in canonical INTERVALS order). */
export function availableIntervals(plans: { prices: PlanPrices }[]): PlanInterval[] {
  return INTERVALS.filter((i) => plans.some((p) => (p.prices[i] ?? 0) > 0));
}

/** Preferred default interval: annual if offered, else monthly, else the first available. */
export function defaultInterval(available: PlanInterval[]): PlanInterval {
  // Monthly is the default everywhere (lowest commitment); fall back to whatever
  // the plan actually offers.
  if (available.includes("monthly")) return "monthly";
  return available[0] ?? "monthly";
}

export interface PlanRecord {
  id: string;
  name: string;
  kind: PlanKind;
  /** Monthly request quota. null = unlimited. */
  monthlyRequests: number | null;
  /** Per-window rate limit. null = unset. */
  rateLimit: number | null;
  /** Monthly AI credit allocation (separate hard pool). null = unlimited/custom. */
  aiCreditLimit: number | null;
  /** Team seat limit (members + pending invites). null = unlimited. */
  seatLimit: number | null;
  prices: PlanPrices;
  /** Over-usage rate: `overageCents` per `overageUnit` requests (null = hard cap). */
  overageCents: number | null;
  overageUnit: number | null;
  /** Per-plan feature entitlements (mirror of PLAN_LIMITS; admin-editable). */
  mcpEnabled: boolean;
  ssoEnabled: boolean;
  auditLogsEnabled: boolean;
  /** Audit-log retention window in days (0 = no pruning / indefinite). */
  auditRetentionDays: number;
  prioritySupportEnabled: boolean;
  customSlaEnabled: boolean;
  /** Historical time-series API (/history) entitlement (Growth+). */
  historyApiEnabled: boolean;
  /** Dodo product id per billing interval (for checkout). Empty until configured. */
  dodoProductIds: Partial<Record<PlanInterval, string>>;
  archived: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Per-plan capabilities derived from PLAN_LIMITS (the source of truth) so the
 * dashboard, marketing site, and docs all show the same thing. Keyed off the
 * public plan id (which equals the tier for standard plans) and `kind`.
 * Custom plans are treated as top-tier (unlimited seats + every feature),
 * since they're bespoke enterprise deals.
 */

/** The standard PlanDefinition for a plan id, or null for custom/unknown ids. */
function tierDef(planId: string): PlanDefinition | null {
  return planId in PLAN_LIMITS ? PLAN_LIMITS[planId as PlanTier] : null;
}

/** Member-seat allowance for a plan. null = unlimited. */
export function planMemberLimit(plan: { id: string; kind?: PlanKind }): number | null {
  if (plan.kind === "custom") return null;
  return tierDef(plan.id)?.seatLimit ?? null;
}

/** Monthly AI credit allocation for a plan. null = unlimited/custom. */
export function planAiCreditLimit(plan: { id: string; kind?: PlanKind }): number | null {
  if (plan.kind === "custom") return null;
  return tierDef(plan.id)?.aiCreditLimit ?? null;
}

/**
 * Per-minute request rate limit for a plan (the native limiter window is 60s).
 * Standard plans use the tier's catalog limit; custom plans use their own
 * configured `rateLimit` (null = fall back to the env/Free default).
 */
export function planRateLimit(plan: { id: string; kind?: PlanKind; rateLimit?: number | null }): number | null {
  if (plan.kind === "custom") return plan.rateLimit ?? null;
  return tierDef(plan.id)?.rateLimit ?? null;
}

/** "600 req/min" style label for a plan's rate limit, or null when there's none. */
export function rateLimitText(plan: { id: string; kind?: PlanKind; rateLimit?: number | null }): string | null {
  const n = planRateLimit(plan);
  return n && n > 0 ? `${n.toLocaleString("en-US")} req/min` : null;
}

/** Whether a plan includes MCP access (every standard plan does). */
export function planHasMcp(plan: { id: string; kind: PlanKind }): boolean {
  return plan.kind === "custom" || (tierDef(plan.id)?.mcpEnabled ?? true);
}

/** Whether a plan includes Enterprise SSO. */
export function planHasSso(plan: { id: string; kind: PlanKind }): boolean {
  return plan.kind === "custom" || (tierDef(plan.id)?.ssoEnabled ?? false);
}

/** Whether a plan includes customer audit logs. */
export function planHasAuditLogs(plan: { id: string; kind: PlanKind }): boolean {
  return plan.kind === "custom" || (tierDef(plan.id)?.auditLogsEnabled ?? false);
}

/**
 * Audit-log retention window (days) for a plan. Custom plans default to the
 * Enterprise window; standard plans use the tier's catalog value. A per-org
 * override (`org_subscriptions.audit_retention_days`) takes precedence at the
 * call site. 0 = no pruning (indefinite).
 */
export function planAuditRetentionDays(plan: { id: string; kind: PlanKind }): number {
  if (plan.kind === "custom") return PLAN_LIMITS.enterprise.auditRetentionDays;
  return tierDef(plan.id)?.auditRetentionDays ?? 90;
}

/** Whether a plan includes priority support. */
export function planHasPrioritySupport(plan: { id: string; kind: PlanKind }): boolean {
  return plan.kind === "custom" || (tierDef(plan.id)?.prioritySupportEnabled ?? false);
}

/** Whether a plan includes a custom SLA. */
export function planHasCustomSla(plan: { id: string; kind: PlanKind }): boolean {
  return plan.kind === "custom" || (tierDef(plan.id)?.customSlaEnabled ?? false);
}

/**
 * Marketing display of a plan's over-usage rate, driven by PLAN_LIMITS:
 *   - fixed paid tier with a rate → e.g. "$0.05 / 1,000 requests"
 *   - enterprise / custom plan     → "Custom" (admin-managed)
 *   - free / any tier with no rate → null (render as "No overage")
 */
export function planOverageText(plan: { id: string; kind?: PlanKind }): string | null {
  if (plan.id === "enterprise" || plan.kind === "custom") return "Custom";
  const cents = tierDef(plan.id)?.overagePer1000Cents ?? null;
  if (cents == null || cents <= 0) return null;
  const dollars = cents / 100;
  const price = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  return `${price} / 1,000 requests`;
}

/** Human label for a plan's overage rate, e.g. "$0.25 per 10,000 requests". */
export function overageRateText(
  cents: number | null | undefined,
  unit: number | null | undefined,
): string | null {
  if (!cents || cents <= 0 || !unit || unit <= 0) return null;
  const dollars = cents / 100;
  const price = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  return `${price} per ${unit.toLocaleString("en-US")} requests`;
}

export interface PlanUsageInfo extends PlanRecord {
  /** Number of orgs with this as their ACTIVE plan. */
  orgCount: number;
}

/** An org this plan is available to (allow-list). */
export interface PlanOrgRef {
  id: string;
  name: string;
  slug: string;
  /** True if this plan is the org's currently-active plan (can't be removed). */
  active: boolean;
}

/** Plan detail incl. the orgs it's available to. */
export interface PlanDetail extends PlanUsageInfo {
  orgs: PlanOrgRef[];
}

/** Create/update a custom plan. */
export interface PlanWriteRequest {
  name: string;
  monthlyRequests: number | null;
  rateLimit: number | null;
  prices: PlanPrices;
  overageCents?: number | null;
  overageUnit?: number | null;
  dodoProductIds?: Partial<Record<PlanInterval, string>>;
  archived?: boolean;
  /** History API (/history) entitlement (Growth+ by default). Admin-editable per plan. */
  historyApiEnabled?: boolean;
}

// Compensation grants ---------------------------------------------------------

export type GrantKind = "once" | "until" | "months";

export interface GrantRequest {
  /** Extra requests per period (0 clears the grant). */
  addRequests: number;
  kind: GrantKind;
  /** For kind=until: last YYYY-MM the grant applies (inclusive). */
  untilPeriod?: string;
  /** For kind=months: number of months the grant runs from the current period. */
  months?: number;
}

export interface GrantInfo {
  requests: number;
  kind: GrantKind;
  /** YYYY-MM start (the period it was granted). */
  startPeriod: string;
  /** YYYY-MM end (inclusive); null for one-time. */
  endPeriod: string | null;
  /** Whether the grant applies to the current period. */
  active: boolean;
}

// Customer-facing plan views --------------------------------------------------

export interface OrgPlanView {
  planId: string;
  label: string;
  kind: PlanKind;
  interval: PlanInterval | null;
  /** Base monthly quota (null = unlimited). */
  monthlyLimit: number | null;
  /** Effective monthly quota incl. the active grant (null = unlimited). */
  effectiveLimit: number | null;
  grant: GrantInfo | null;
  prices: PlanPrices;
  /** Over-usage rate (null = hard cap, no overage). */
  overageCents: number | null;
  overageUnit: number | null;
  /** Pending cancel-at-period-end target (e.g. "free"), or null if none. */
  scheduledPlan?: string | null;
  /** ISO datetime the scheduled downgrade applies. */
  scheduledAt?: string | null;
  /** Subscription billing status: active | past_due | canceled. */
  status?: string;
  /** Effective customer audit-log entitlement (plan default + per-org override). */
  auditLogsEnabled?: boolean;
  /**
   * Accrued, not-yet-charged API-request overage (cents). Multi-month plans
   * (annual/quarterly) bill this monthly via an on-demand charge; this is the
   * pending amount before it's collected. 0 for monthly plans (metered) and
   * plans with no overage.
   */
  overageOwedCents?: number;
  /** The org's selected billing region (ISO 3166-1 alpha-2), if set. */
  billingCountry?: string | null;
}

export interface OrgPlansResponse {
  current: OrgPlanView;
  /** Public standard plans (catalog). */
  publicPlans: PlanRecord[];
  /** Custom plans on this org's allow-list (visible only to it). */
  customPlans: PlanRecord[];
  /** The requesting member's role — gates billing/plan actions in the UI. */
  viewerRole?: OrgRole;
}

// ---------------------------------------------------------------------------
// Admin app DTOs (admin.currency-core.com) — not part of the public API
// ---------------------------------------------------------------------------

/** Generic paginated list envelope. */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminListQuery {
  q?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc";
}

/** Snapshot of the managed datasets + tenant counts for the admin home. */
export interface DataStatus {
  rates: { latestDate: string | null; currencyCount: number; totalRows: number };
  ppp: {
    years: { min: number; max: number } | null;
    rows: number;
    projected: number;
    countries: number;
  };
  counts: { organizations: number; users: number; apiKeys: number; bannedUsers: number };
  /** MCP server usage: MCP-sourced requests this period + orgs connected ≥ once. */
  mcp: { requestsThisPeriod: number; connectedOrgs: number };
  /** AI endpoint usage: AI-sourced requests this period. */
  ai: { requestsThisPeriod: number };
}

export type OrgStatus = "active" | "past_due" | "canceled";
export type OrgRole = "owner" | "admin" | "member";

export interface AdminOrgSummary {
  id: string;
  name: string;
  slug: string;
  /** Display tier for badge/color (custom plans -> "custom"). */
  plan: OrgPlan;
  /** Plan record id (free|pro|...|plan_<x>). */
  planId: string;
  /** Display label (custom label when plan = custom). */
  planLabel: string;
  status: OrgStatus;
  memberCount: number;
  /** Requests used in the current YYYY-MM period. */
  currentUsage: number;
  /** Base monthly quota (null = unlimited). */
  monthlyLimit: number | null;
  /** Effective monthly quota incl. the active grant (null = unlimited). */
  effectiveMonthlyLimit: number | null;
  createdAt: string | null;
}

export interface AdminMember {
  userId: string;
  name: string | null;
  email: string;
  role: OrgRole;
  banned: boolean;
  joinedAt: string | null;
}

export interface AdminOrgDetail extends AdminOrgSummary {
  members: AdminMember[];
  subscription: {
    plan: OrgPlan;
    planId: string;
    planKind: PlanKind;
    status: OrgStatus;
    billingInterval: PlanInterval | null;
    customLabel: string | null;
    currentPeriodEnd: string | null;
    dodoCustomerId: string | null;
    dodoSubscriptionId: string | null;
    /** Compensation grant: amount + schedule (bonusPeriod = start YYYY-MM). */
    bonusRequests: number | null;
    bonusPeriod: string | null;
    grantKind: GrantKind | null;
    grantEndPeriod: string | null;
  };
  /** Org's own hard cap (null = off). */
  hardCapRequests?: number | null;
  /** Admin ceiling (null = off). */
  adminHardCapRequests?: number | null;
  /** Resolved compensation grant (null if none configured). */
  grant: GrantInfo | null;
  /** Recent daily usage (ascending) for a sparkline. */
  recentUsage: UsagePoint[];
  /** AI-sourced requests used in the current period. */
  currentUsageAi: number;
  /** Whether the AI endpoint is enabled for this org (admin-toggleable). */
  aiEnabled: boolean;
  /** Effective customer audit-log entitlement (plan default + per-org override). */
  auditLogsEnabled: boolean;
  /** Audit-logs override: null = inherit from plan, true = force on, false = force off. */
  auditLogsOverride: boolean | null;
  /** Effective audit retention window in days (per-org override ?? plan default). */
  auditRetentionDays: number;
}

/** `refund_pending` = an admin started a Dodo refund that hasn't settled yet. */
export type InvoiceStatus = "paid" | "open" | "void" | "refunded" | "refund_pending";

export interface InvoiceDTO {
  id: string;
  date: string | null;
  amountCents: number;
  currency: string;
  status: InvoiceStatus;
  description: string | null;
  hostedUrl: string | null;
  /** True when this invoice can still be refunded (paid, not already refunding). */
  refundable: boolean;
}

/** Admin-initiated refund of a single paid invoice. */
export interface RefundRequest {
  invoiceId: string;
  reason?: string;
}

// ---------------------------------------------------------------------------
// Customer audit log (org_audit_logs) — B2B per-organization audit trail
// ---------------------------------------------------------------------------

export type AuditCategory =
  | "api_key"
  | "team"
  | "billing"
  | "security"
  | "settings"
  | "admin"
  | "api_activity";

export type AuditActorType = "member" | "admin" | "system";

export interface AuditEventDef {
  category: AuditCategory;
  /** Human-readable past-tense label, e.g. "Revoked API key". */
  label: string;
}

/**
 * Canonical registry of every audit event. Category is DERIVED from this map
 * (callers pass only the event key — see logOrgEvent), and the dashboard reads
 * labels + builds its filter options from it, so the API and UI never drift.
 */
export const AUDIT_EVENTS = {
  // API keys
  "key.created": { category: "api_key", label: "Created API key" },
  "key.revoked": { category: "api_key", label: "Revoked API key" },
  "key.rotated": { category: "api_key", label: "Rotated demo key" },
  "key.revealed": { category: "api_key", label: "Revealed API key" },
  // Team & members
  "member.invited": { category: "team", label: "Invited member" },
  "member.joined": { category: "team", label: "Member joined" },
  "invite.canceled": { category: "team", label: "Canceled invitation" },
  "member.removed": { category: "team", label: "Removed member" },
  "member.left": { category: "team", label: "Member left" },
  "member.role_changed": { category: "team", label: "Changed member role" },
  "ownership.transferred": { category: "team", label: "Transferred ownership" },
  // Billing & plans
  "plan.upgraded": { category: "billing", label: "Upgraded plan" },
  "plan.downgraded": { category: "billing", label: "Downgraded plan" },
  "plan.scheduled_downgrade": { category: "billing", label: "Scheduled downgrade" },
  "subscription.canceled": { category: "billing", label: "Canceled subscription" },
  "subscription.resumed": { category: "billing", label: "Resumed subscription" },
  "payment.succeeded": { category: "billing", label: "Payment succeeded" },
  "payment.failed": { category: "billing", label: "Payment failed" },
  "ai_credits.purchased": { category: "billing", label: "Purchased AI credits" },
  "refund.issued": { category: "billing", label: "Refund issued" },
  "account.on_hold": { category: "billing", label: "Account placed on hold" },
  "account.reactivated": { category: "billing", label: "Account reactivated" },
  "billing.portal_opened": { category: "billing", label: "Opened billing portal" },
  "billing.region_changed": { category: "billing", label: "Changed billing region" },
  "overage.paid": { category: "billing", label: "Paid API overage" },
  // Security
  "sso.provider_registered": { category: "security", label: "Registered SSO provider" },
  "sso.verified": { category: "security", label: "Verified SSO provider" },
  "sso.deleted": { category: "security", label: "Deleted SSO provider" },
  "sso.enforcement_changed": { category: "security", label: "Changed SSO enforcement" },
  // Settings
  "mcp.toggled": { category: "settings", label: "Toggled MCP access" },
  "ai_endpoint.toggled": { category: "settings", label: "Toggled AI endpoint" },
  "hard_cap.set": { category: "settings", label: "Set spend hard cap" },
  "hard_cap.cleared": { category: "settings", label: "Cleared spend hard cap" },
  "org.created": { category: "settings", label: "Created organization" },
  "org.renamed": { category: "settings", label: "Renamed organization" },
  "byok.settings_changed": { category: "settings", label: "Changed BYOK settings" },
  "byok.provider_added": { category: "settings", label: "Added a BYOK provider" },
  "byok.provider_updated": { category: "settings", label: "Updated a BYOK provider" },
  "byok.provider_removed": { category: "settings", label: "Removed a BYOK provider" },
  // Admin actions on the account (shown to the customer as "CurrencyCore support")
  "admin.plan_changed": { category: "admin", label: "Support changed plan" },
  "admin.grant_applied": { category: "admin", label: "Support applied grant" },
  "admin.ai_credits_granted": { category: "admin", label: "Support granted AI credits" },
  "admin.refund_issued": { category: "admin", label: "Support issued refund" },
  "admin.ai_toggled": { category: "admin", label: "Support toggled AI endpoint" },
  "admin.mcp_toggled": { category: "admin", label: "Support toggled MCP access" },
  "admin.hard_cap_changed": { category: "admin", label: "Support changed hard-cap ceiling" },
  "admin.audit_logs_changed": { category: "admin", label: "Support changed audit-log access" },
  // API activity (anomalies + credit-consuming AI calls; filterable category so
  // it never buries the account-level events)
  "rate_limit.exceeded": { category: "api_activity", label: "Rate limit exceeded" },
  "quota.exceeded": { category: "api_activity", label: "Monthly quota exceeded" },
  "request.blocked": { category: "api_activity", label: "Request blocked (account on hold)" },
  "key.used_after_revoke": { category: "api_activity", label: "Revoked key used" },
  "ai.request": { category: "api_activity", label: "AI request" },
} as const satisfies Record<string, AuditEventDef>;

export type AuditEvent = keyof typeof AUDIT_EVENTS;

/** Display label per category for the filter UI. */
export const AUDIT_CATEGORY_LABELS: Record<AuditCategory, string> = {
  api_key: "API Keys",
  team: "Team",
  billing: "Billing",
  security: "Security",
  settings: "Settings",
  admin: "Support",
  api_activity: "API Activity",
};

/** Categories in display order. */
export const AUDIT_CATEGORIES: AuditCategory[] = [
  "api_key",
  "team",
  "billing",
  "security",
  "settings",
  "admin",
  "api_activity",
];

/** Resolve an event's category + label, falling back gracefully for unknown keys. */
export function auditEventDef(event: string): AuditEventDef {
  return (AUDIT_EVENTS as Record<string, AuditEventDef>)[event] ?? { category: "settings", label: event };
}

/** One row of the customer audit log. */
export interface OrgAuditEvent {
  id: string;
  category: AuditCategory;
  event: string;
  /** Human-readable label resolved from AUDIT_EVENTS. */
  eventLabel: string;
  actorType: AuditActorType;
  actorId: string | null;
  actorName: string | null;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  /** Parsed detail object (amounts, before/after, …). */
  detail: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
}

/** Cursor-paginated audit log page. */
export interface AuditLogPage {
  events: OrgAuditEvent[];
  /** Opaque cursor for the next (older) page; null when exhausted. */
  nextCursor: string | null;
}

/** Distinct actors + event types occurring for an org, to populate the filter UI. */
export interface AuditLogMeta {
  actors: { id: string; name: string; type: AuditActorType }[];
  events: string[];
  /** Whether the org is currently entitled to view the audit log. */
  enabled: boolean;
  /** Effective retention window in days (for the "events older than … are pruned" note). */
  retentionDays: number;
}

/** A country Dodo supports for billing (ISO 3166-1 alpha-2 + display name). */
export interface SupportedCountry {
  code: string;
  name: string;
}

/** Supported billing countries + the org's selected region. */
export interface BillingCountriesResponse {
  countries: SupportedCountry[];
  /** Selected region code (manual, auto-detected from IP, or the default). */
  selected: string;
}

export interface BillingSummary {
  organizationId: string;
  plan: OrgPlan;
  planLabel: string;
  status: OrgStatus;
  currency: string;
  /** Sum of paid invoices (cents). */
  totalPaidCents: number;
  invoiceCount: number;
  currentPeriodEnd: string | null;
  dodoCustomerId: string | null;
  dodoSubscriptionId: string | null;
  invoices: InvoiceDTO[];
}

export interface AdminUserOrgRef {
  organizationId: string;
  organizationName: string;
  role: OrgRole;
  plan: OrgPlan;
  planId: string;
}

export interface AdminUserSummary {
  id: string;
  name: string | null;
  email: string;
  emailVerified: boolean;
  banned: boolean;
  bannedReason: string | null;
  createdAt: string | null;
  orgCount: number;
  /** Highest plan across the user's orgs, for quick filtering. */
  topPlan: OrgPlan | null;
}

export interface AdminUserDetail extends AdminUserSummary {
  bannedAt: string | null;
  organizations: AdminUserOrgRef[];
}

/** Assign a plan to an org. The billing interval is locked once set. */
export interface SetPlanRequest {
  planId: string;
  /** Required when the plan has prices (i.e. not free); chosen at assignment. */
  billingInterval?: PlanInterval;
  status?: OrgStatus;
}

// --- Usage analytics -------------------------------------------------------

export type UsageGranularity = "day" | "week" | "month";

export interface UsagePoint {
  /** Bucket label: YYYY-MM-DD (day), YYYY-Www (week), or YYYY-MM (month). */
  bucket: string;
  requests: number;
}

export interface NamedSeries {
  key: string;
  label: string;
  points: UsagePoint[];
}

export interface UsageAnalytics {
  range: { from: string; to: string; granularity: UsageGranularity };
  totals: {
    requests: number;
    activeOrgs: number;
    /** AI-sourced requests in the window. */
    aiRequests: number;
    /** % change vs the preceding equal-length window (null if no baseline). */
    growthPct: number | null;
  };
  /** Total requests over time (single series). */
  overTime: UsagePoint[];
  /** Requests split by plan (one series per plan). */
  byPlan: NamedSeries[];
  /** Top organizations by requests in the window. */
  topOrgs: { organizationId: string; name: string; requests: number }[];
}

// ---------------------------------------------------------------------------
// Routes / storage keys (shared by client + server)
// ---------------------------------------------------------------------------

/** API route paths served by the Hono worker at api.currency-core.com. */
export const API_ROUTES = {
  health: "/health",
  healthAlias: "/api/health",
  convert: "/convert",
  rates: "/rates",
  ppp: "/ppp",
  countries: "/countries",
  currencies: "/currencies",
  authBase: "/api/auth",
  orgUsage: "/orgs/:orgId/usage",
  orgUsageDaily: "/orgs/:orgId/usage/daily",
  orgUsageKeys: "/orgs/:orgId/usage/keys",
  orgOverage: "/orgs/:orgId/overage",
  orgAi: "/orgs/:orgId/ai",
  apiKeys: "/orgs/:orgId/keys",
  billingCheckout: "/billing/checkout",
  billingPreview: "/billing/preview",
  billingPortal: "/billing/portal",
  billingCancel: "/billing/cancel",
  billingResume: "/billing/resume",
  billingCountries: "/billing/countries",
  billingRegion: "/billing/region",
  dodoWebhook: "/webhooks/dodo",
  status: "/status",
  adminStatusIncidents: "/admin/status/incidents",
  adminStatusSummary: "/admin/status/summary",
} as const;

/** Browser localStorage keys used by the dashboard SPA. */
export const STORAGE_KEYS = {
  /** Sticky workspace redirection engine — last active organization id. */
  lastOrgId: "currencycore:last_org_id",
} as const;

/** Canonical KV key builders. Used by the API hot path and the cron worker. */
export const kvKeys = {
  /** rates:YYYY-MM-DD -> RatesMap */
  rates: (date: IsoDate): string => `rates:${date}`,
  /** history:<CCY> -> { "YYYY-MM-DD": effectiveUsdRate } (per-currency daily series; USD omitted, always 1) */
  history: (currency: CurrencyCode): string => `history:${currency.toUpperCase()}`,
  /** analytics:usd:<window> -> precomputed per-currency-vs-USD metrics for one standard window */
  analyticsUsd: (window: string): string => `analytics:usd:${window}`,
  /** ppp:YYYY:CC -> number */
  ppp: (year: number, country: CountryCode): string =>
    `ppp:${year}:${country.toUpperCase()}`,
  /** countries:list -> Country[] (single-row KV cache) */
  countries: "countries:list",
  /** currencies:list -> Currency[] (single-row KV cache) */
  currencies: "currencies:list",
} as const;

// ---------------------------------------------------------------------------
// Pure helpers (no runtime deps) usable on client + server
// ---------------------------------------------------------------------------

/** Format a Date (or now) as YYYY-MM-DD in UTC. */
export function toIsoDate(d: Date = new Date()): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** Normalize a currency code to upper-case ISO 4217 form. */
export function normalizeCurrency(code: string): CurrencyCode {
  return code.trim().toUpperCase();
}

/** Normalize a country code to upper-case ISO 3166-1 alpha-2 form. */
export function normalizeCountry(code: string): CountryCode {
  return code.trim().toUpperCase();
}

// Public API catalog (shared with the dashboard Playground + MCP server).
export * from "./api-catalog";
